import { flatten, uniq } from 'lodash';

import type {
  CustomRequirementConditionType,
  CustomRequirementType
} from '@/components/CheckRequirements/CheckRequirements.types';
import { PERMISSIONS } from '@/constants/auth.constants';
import { PERMISSIONS_LOGIC } from '@/constants/common';
import type { PreferencesDTO } from '@/pages/Dashboard/DashboardMCSC/DashboardMCSC.types';
import type { Organization, ProductDTO } from '@/pages/Organizations';
import type { UserInfo } from '@/types/common';

export const checkPermissions = (
  permissions: string | string[],
  user: UserInfo | null,
  selectedOrganization: Organization,
  logic?: PERMISSIONS_LOGIC
): boolean => {
  let orgPermissions = user?.permissions[selectedOrganization.label] || [];

  // Add 'all' permission if user has 'All' organization (partner orgs and internal user) or 'not_all' if user doesn't have 'All' organization
  if (user?.permissions.all && !orgPermissions.includes(PERMISSIONS.ALL)) {
    orgPermissions.push(PERMISSIONS.ALL);
  } else if (
    !user?.permissions.all &&
    !orgPermissions.includes(PERMISSIONS.NOT_ALL)
  ) {
    orgPermissions.push(PERMISSIONS.NOT_ALL);
  }

  // Add 'internal' permission if user is internal or 'not_internal' if user is not internal
  if (user?.internal && !orgPermissions.includes(PERMISSIONS.INTERNAL)) {
    orgPermissions.push(PERMISSIONS.INTERNAL);
  } else if (
    !user?.internal &&
    !orgPermissions.includes(PERMISSIONS.NOT_INTERNAL)
  ) {
    orgPermissions.push(PERMISSIONS.NOT_INTERNAL);
  }

  if (
    user?.organizations.length === 1 &&
    selectedOrganization.label === 'all'
  ) {
    orgPermissions = orgPermissions.concat(
      user.permissions[user.organizations[0]]
    );
  }

  const ponhqPermissions = user?.permissions.ponhq || [];

  const permissionsList = user?.internal
    ? uniq([...orgPermissions, ...ponhqPermissions])
    : orgPermissions;

  // If permissions is a string, check if it is in the permissions list
  if (typeof permissions === 'string') {
    return permissionsList?.includes(permissions) || permissions === 'exempt';
  }

  // If permissions is an array, check if logic is 'OR', check if any permissions are in the permissions list
  if (logic === PERMISSIONS_LOGIC.OR) {
    return permissions.some((perm: string) => permissionsList?.includes(perm));
  }
  // Default 'AND' logic, check if all permissions are in the permissions list
  return permissions.every((perm: string) => permissionsList?.includes(perm));
};

export const checkProducts = (
  orgProducts: ProductDTO[],
  requiredProducts?: string[],
  logic?: PERMISSIONS_LOGIC
) => {
  if (
    requiredProducts?.length === 0 ||
    !requiredProducts ||
    requiredProducts.includes('exempt')
  ) {
    return true; // Show item if no required products
  }

  const allProductsInOrg = orgProducts.map(
    (product: ProductDTO) => product.product_type
  );

  if (logic === PERMISSIONS_LOGIC.OR) {
    return requiredProducts?.some((requiredProduct: string) =>
      allProductsInOrg.includes(requiredProduct)
    );
  }

  // By default, use 'AND' logic
  const hasRequiredProducts = requiredProducts?.every(
    (requiredProduct: string) => allProductsInOrg.includes(requiredProduct)
  );

  if (logic === PERMISSIONS_LOGIC.NOT) {
    return !hasRequiredProducts;
  }

  return hasRequiredProducts; // Only show item if org has required products
};

export const checkProductSubTypes = (
  orgProducts: ProductDTO[],
  requiredProductSubTypes?: string[],
  logic?: PERMISSIONS_LOGIC
) => {
  if (
    !requiredProductSubTypes ||
    requiredProductSubTypes.includes('exempt') ||
    orgProducts.length === 0
  ) {
    return true; // Show item if no required product sub type
  }

  const allProductSubTypesInOrg = orgProducts.map(
    (product: ProductDTO) => product.sub_type
  );
  if (logic === PERMISSIONS_LOGIC.OR) {
    return requiredProductSubTypes?.some((requiredSubType: string) =>
      allProductSubTypesInOrg.includes(requiredSubType)
    );
  }

  // By default, use 'AND' logic
  const hasRequiredProductSubTypes = requiredProductSubTypes?.every(
    (requiredSubType: string) =>
      allProductSubTypesInOrg.includes(requiredSubType)
  );

  if (logic === PERMISSIONS_LOGIC.NOT) {
    return !hasRequiredProductSubTypes;
  }
  return hasRequiredProductSubTypes; // Only show item if org has required product sub types
};

export const checkUserPreferences = (
  user: UserInfo | null,
  preference?: Array<keyof PreferencesDTO>,
  logic?: PERMISSIONS_LOGIC
) => {
  if (!preference || preference.length === 0 || !user?.preferences) {
    return true; // If no preferences are specified or if user has no preferences, return true
  }

  if (!user) {
    return false; // If user is not defined, return false
  }

  if (logic === PERMISSIONS_LOGIC.OR) {
    // Check if any of the preferences are set to true
    return preference.some((pref) => user.preferences?.[pref] === true);
  }

  // Check if the user has the specified preference set to true
  return preference.every((pref) => user.preferences?.[pref] === true);
};

/*
  This function checks if a user meets custom requirements based on their permissions, products, and product sub types
  It allows for any number of conditions to be specified, each with its own logic (AND/OR).
  The overall logic (AND/OR) determines how the conditions are combined.
*/
export const checkCustomRequirements = (
  user: UserInfo | null,
  selectedOrganization: Organization,
  orgProducts: ProductDTO[],
  customRequirements?: CustomRequirementType
) => {
  if (!customRequirements) {
    return true; // If no custom requirements are specified, return true
  }
  const logic = customRequirements?.logic || PERMISSIONS_LOGIC.AND; // Default to AND logic
  const conditions = customRequirements?.conditions || [];

  if (conditions.length === 0) {
    return true; // If no conditions are specified, return true
  }

  // ------ Getting all permissions strings -----------
  let orgPermissions = user?.permissions[selectedOrganization.label] || [];

  // Add 'all' permission if user has 'All' organization (partner orgs and internal user) or 'not_all' if user doesn't have 'All' organization
  if (user?.permissions.all && !orgPermissions.includes(PERMISSIONS.ALL)) {
    orgPermissions.push(PERMISSIONS.ALL);
  } else if (
    !user?.permissions.all &&
    !orgPermissions.includes(PERMISSIONS.NOT_ALL)
  ) {
    orgPermissions.push(PERMISSIONS.NOT_ALL);
  }

  // Add 'internal' permission if user is internal or 'not_internal' if user is not internal
  if (user?.internal && !orgPermissions.includes(PERMISSIONS.INTERNAL)) {
    orgPermissions.push(PERMISSIONS.INTERNAL);
  } else if (
    !user?.internal &&
    !orgPermissions.includes(PERMISSIONS.NOT_INTERNAL)
  ) {
    orgPermissions.push(PERMISSIONS.NOT_INTERNAL);
  }

  if (
    user?.organizations.length === 1 &&
    selectedOrganization.label === 'all'
  ) {
    orgPermissions = orgPermissions.concat(
      user.permissions[user.organizations[0]]
    );
  }

  const ponhqPermissions = user?.permissions.ponhq || [];

  const permissionsList = user?.internal
    ? uniq([...orgPermissions, ...ponhqPermissions])
    : orgPermissions;

  // ----- End of permissions setting logic -------

  // ------ Getting all products strings -----------

  const allProductsInOrg = orgProducts.map(
    (product: ProductDTO) => product.product_type
  );

  // ----- End of products setting logic -------

  // ------ Getting all product sub types strings -----------

  const allProductSubTypesInOrg = orgProducts.map(
    (product: ProductDTO) => product.sub_type
  );

  // ----- End of product sub types setting logic -------

  const allPossibleConditions = flatten([
    permissionsList,
    allProductsInOrg,
    allProductSubTypesInOrg
  ]);

  const conditionsMet = conditions.map(
    (conditionObj: CustomRequirementConditionType) => {
      const condition = conditionObj.condition || [];
      const conditionLogic = conditionObj.logic || PERMISSIONS_LOGIC.AND;

      if (conditionLogic === PERMISSIONS_LOGIC.OR) {
        return condition.some((cond: string) =>
          allPossibleConditions.includes(cond)
        );
      }
      return condition.every((cond: string) =>
        allPossibleConditions.includes(cond)
      );
    }
  );

  // Check if OR logic: if at least one condition is met, return true
  if (logic === PERMISSIONS_LOGIC.OR) {
    return conditionsMet.some(Boolean); // If at least one condition is met, return true
  }

  return conditionsMet.every(Boolean); // All conditions must be met to return true
};

export default checkPermissions;
